import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Utils } from '../../core/utils';
import { SelectItem } from "primeng/api";
import { AppStateService } from "ng/src/app/core/services/app-state.service";
import { ButtonState, NoteColumn, NotesTableDataRow } from './notes-table.model';
import { TableExtended } from '../../shared/model/primeng-extensions.model';
import { NotesTableService } from './notes-table.service';
import { Report } from '../../reports/report.model';
import { PDFExportOptions } from '../../shared/pdf-export/pdf-export.model';
import { LogService } from '../../logs/log.service';
import { NoteExportData } from '../shared/note-export.model';
import { NoteExportService } from '../shared/note-export.service';
import { fromMediaQuery, MediaQueries } from "ng/src/app/shared/media-query-observable";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";

@Component({
	selector: 'lb-notes-table-caption',
	templateUrl: './notes-table-caption.component.html',
	styleUrls: ['./notes-table-caption.component.css']
})
export class NotesTableCaptionComponent implements OnInit, OnDestroy {
	@Input() columnKeys!: NoteColumn[];
	@Input() downloadCSV?: (showComments: boolean) => void;
	@Input() enableAutoScroll!: boolean;
	@Input() enableReportFiltering = true;
	@Input() exportMessage = '';
	@Input() getPDFReportQueryParams!: () => { [queryParam: string]: string };
	@Input() lastUpdate?: { date: Date, success: boolean };
	@Input() loading = false;
	@Input() notesMessage!: string;
	@Input() pollBased = true;
	@Input() savedPreferencesKey!: string;
	@Input() scrollTop = 0;
	@Input() showMaximizeButton = false;
	@Input() sortMode?: 'single' | 'multiple';
	@Input() table!: TableExtended;
	@Input() turnoverFilter = false;
	@Input() turnoverTooltip = '';
	@Input() useNoteActions = true;

	@Input()
	get buttonState() {
		const buttonState = !AppStateService.hoverSupported && this._buttonState == 'HOVER' ?
			'VISIBLE' :
			this._buttonState;
		
		return buttonState;
	}
	set buttonState(val) {
		this._buttonState = val;

		AppStateService.preferences.setValue(this.savedButtonStateKey, this.buttonState);
	}

	@Input()
	get maximized() {
		return this._maximized
	}
	set maximized(val) {
		this._maximized = val;
		this.maximizedChange.emit(val);
	}

	@Output() cancelQueryClicked = new EventEmitter();
	@Output() columnToggled = new EventEmitter();
	@Output() expandCollapseContentClicked = new EventEmitter<boolean>();
	@Output() maximizedChange = new EventEmitter<boolean>();
	@Output() refreshQueryClicked = new EventEmitter();
	@Output() reportFilterChanged = new EventEmitter<number | null>();
	@Output() showDefaultColumnsClicked = new EventEmitter();
	@Output() turnoverFilterChanged = new EventEmitter<boolean>();

	
	actionButtonOptions: SelectItem[] = [];
	reportFilters!: Report[];
	showExportDialog = false;
	viewSelection = 'auto';
	viewSelectionOptions: SelectItem[] = [
		{ label: 'Card', value: 'card' },
		{ label: 'Auto', value: 'auto' },
	];

	get activeNoteFilterReportId()  {
		return this.notesTableService.activeNoteFilterReportId;
	}
	set activeNoteFilterReportId(reportId) {
		this.notesTableService.activeNoteFilterReportId = reportId;
	}

	get defaultToAbridged(): boolean {
        return AppStateService.preferences.getValue(NotesTableService.defaultToAbridgedKey, true);
    }
    set defaultToAbridged(val) {
        AppStateService.preferences.setValue(NotesTableService.defaultToAbridgedKey, val);
	}

	get hoverSupported() {
		return AppStateService.hoverSupported;
	}

	get lastUpdateFormatString() {
		return !this.lastUpdate?.date || !Utils.isToday(this.lastUpdate.date) ?
			'MM/dd/yyyy HH:mm:ss' :
			'HH:mm:ss';
	}

	get notes(): NotesTableDataRow[] {
		return this.table.value;
	}

	get showVerticalView() {
		return this.viewSelection == 'auto' ?
			this._showVerticalView :
			this.viewSelection == 'card';
	}
	set showVerticalView(val) {
		this._showVerticalView = val;
	}

	private _buttonState!: ButtonState;
	private _maximized = false;
	private _showVerticalView!: boolean;
	private readonly destroySubject = new Subject<void>();
	readonly onDestroy$ = this.destroySubject.asObservable();

	private get savedButtonStateKey() {
		return `${this.savedPreferencesKey}-buttonState`;
	}
	private get savedViewKey() {
		return `${this.savedPreferencesKey}-view`;
	}

	constructor(
		private notesTableService: NotesTableService,
		private noteExportService: NoteExportService
	) { }

	ngOnInit() {
		this.enableReportFiltering && this.notesTableService.getReportFilters().subscribe(reportFilters => {
			this.reportFilters = reportFilters;
		});

		let btnState = AppStateService.preferences.getValue(this.savedButtonStateKey);
		if (!btnState)
			btnState = AppStateService.hoverSupported ? 'HOVER' : 'VISIBLE';

		this.buttonState = btnState;
		this.viewSelection = AppStateService.preferences.getValue(this.savedViewKey, 'auto');
		
		fromMediaQuery(MediaQueries.HOVER_SUPPORTED)
			.pipe(
				takeUntil(this.onDestroy$)
			)
			.subscribe(() => this.updateNoteActionButtons());
		
		this.updateNoteActionButtons();
	}
	
	ngOnDestroy(): void {
		this.destroySubject.next();
		this.destroySubject.complete();
	}

	onExportToPDFClicked(pdfExportOptions: PDFExportOptions) {
		const reportQueryParams = this.getPDFReportQueryParams();
		if (this.activeNoteFilterReportId)
			reportQueryParams.Report = this.reportFilters.find(rf => rf.id == this.activeNoteFilterReportId)!.name;

		this.noteExportService.exportToPDF(new NoteExportData(
			this.notes,
			`${LogService.selectedLog!.name} - ${this.notesMessage}`,
			reportQueryParams,
			pdfExportOptions,
			NoteExportData.getExportColumns(this.columnKeys)
		));
	}

	onCancelQueryClicked() {
		this.cancelQueryClicked.emit();
	}

	onRefreshQueryClicked() {
		this.refreshQueryClicked.emit();
	}

	onReportFilterChanged() {
		const reportId = typeof this.activeNoteFilterReportId == 'number' ? this.activeNoteFilterReportId : null;
		
		this.reportFilterChanged.emit(reportId);
	}

	onExpandCollapseContentClicked(expand: boolean) {
		this.expandCollapseContentClicked.emit(expand);
	}

	onColumnToggled() {
		this.columnToggled.emit();	
	}

	onShowDefaultColumnsClicked() {
		this.showDefaultColumnsClicked.emit();
	}

	onTurnoverFilterChanged() {
		this.turnoverFilterChanged.emit(this.turnoverFilter);
	}
	
	saveViewSelection() {
		AppStateService.preferences.setValue(this.savedViewKey, this.viewSelection);
	}

	scrollToTop() {
        this.table!.resetScrollTop();
    }
	
	private updateNoteActionButtons() {
		const options: SelectItem[] = [
			{ label: 'Visible', value: 'VISIBLE' },
			{ label: 'Disabled', value: 'DISABLED' }
		];
		
		AppStateService.hoverSupported && options.unshift({ label: 'Hover', value: 'HOVER' });
		this.actionButtonOptions = options;
	}
}